/**
 * Pushkaralu (Godavari Pushkarams 2027) shared constants.
 *
 * A work is tagged as a Pushkaralu work via the `is_pushkaralu_in` flag on
 * ds_wms.wms_wrk_lst_t (set from the "Event Tag" dropdown on the existing
 * Create Work form). Tagged works show ONLY in the Pushkaralu dashboard
 * (/app/ds-wms/pushkaralu) and are excluded from every normal WMS dashboard
 * and the Works list.
 *
 * The "Activity" dimension (Ghat Infrastructure, Water Supply, …) is a
 * Pushkaralu-only field stored in `pushkaralu_actvty_cd`. These codes are the
 * source of truth shared by the Create Work form and the dashboard grouping.
 */

export interface PushkaraluActivity {
  cd: string;
  nm: string;
}

// The 13 monitoring activities shown on the dashboard. `cd` is what gets
// stored on the work; `nm` is the display label.
export const PUSHKARALU_ACTIVITIES: PushkaraluActivity[] = [
  { cd: 'GHAT', nm: 'Ghat Infrastructure & Crowd Management' },
  { cd: 'TRFC', nm: 'Traffic & Transportation' },
  { cd: 'ROAD', nm: 'Roads & Bridges' },
  { cd: 'WTR', nm: 'Water Supply' },
  { cd: 'DRN', nm: 'Drainage' },
  { cd: 'SANI', nm: 'Sanitation & Waste Management' },
  { cd: 'LGHT', nm: 'Lighting' },
  { cd: 'BEAU', nm: 'Beautification' },
  { cd: 'SMRT', nm: 'Smart Infrastructure' },
  { cd: 'PLGR', nm: 'Pilgrim Facilities' },
  { cd: 'HLTH', nm: 'Health & Medical Facilities' },
  { cd: 'PARK', nm: 'Parking & Traffic Management' },
  { cd: 'GOVN', nm: 'Urban Governance & Others' },
];

export const PUSHKARALU_ACTIVITY_LABEL: Record<string, string> = Object.fromEntries(
  PUSHKARALU_ACTIVITIES.map((a) => [a.cd, a.nm])
);

// Per-activity icon + colour styling, used by the dashboard's Recent Updates feed
// (and anywhere an activity needs a visual glyph). Falls back to GOVN.
export interface PushkaraluActivityStyle { icon: string; color: string; bg: string; }
export const PUSHKARALU_ACTIVITY_STYLE: Record<string, PushkaraluActivityStyle> = {
  GHAT: { icon: 'ri-building-2-line', color: 'text-orange-600', bg: 'bg-orange-50' },
  TRFC: { icon: 'ri-traffic-light-line', color: 'text-amber-600', bg: 'bg-amber-50' },
  ROAD: { icon: 'ri-road-map-line', color: 'text-stone-600', bg: 'bg-stone-100' },
  WTR: { icon: 'ri-drop-line', color: 'text-cyan-600', bg: 'bg-cyan-50' },
  DRN: { icon: 'ri-water-flash-line', color: 'text-sky-600', bg: 'bg-sky-50' },
  SANI: { icon: 'ri-recycle-line', color: 'text-emerald-600', bg: 'bg-emerald-50' },
  LGHT: { icon: 'ri-lightbulb-line', color: 'text-yellow-600', bg: 'bg-yellow-50' },
  BEAU: { icon: 'ri-plant-line', color: 'text-green-600', bg: 'bg-green-50' },
  SMRT: { icon: 'ri-cpu-line', color: 'text-indigo-600', bg: 'bg-indigo-50' },
  PLGR: { icon: 'ri-community-line', color: 'text-violet-600', bg: 'bg-violet-50' },
  HLTH: { icon: 'ri-first-aid-kit-line', color: 'text-rose-600', bg: 'bg-rose-50' },
  PARK: { icon: 'ri-parking-box-line', color: 'text-blue-600', bg: 'bg-blue-50' },
  GOVN: { icon: 'ri-government-line', color: 'text-slate-600', bg: 'bg-slate-100' },
};
export const activityStyle = (cd?: string | null): PushkaraluActivityStyle =>
  (cd && PUSHKARALU_ACTIVITY_STYLE[cd]) || PUSHKARALU_ACTIVITY_STYLE.GOVN;

// The event tag value stored on the work. A dropdown (not a toggle) so more
// events can be added later without touching the form logic.
export const PUSHKARALU_TAG = 'PUSHKARALU';

// The 6 host UDAs for Godavari Pushkarams 2027. Used to restrict the UDA
// dropdown on the Create Work form when the Pushkaralu tag is selected. Matched
// case-insensitively against admn_unt_nm (spellings vary in the master).
export const PUSHKARALU_ULB_NAMES = [
  'Rajamahendravaram',
  'Rajahmundry',
  'Kovvur',
  'Nidadavolu',
  'Narasapur',
  'Palacole',
  'Mandapeta',
];

export const isPushkaraluUlb = (name?: string | null): boolean => {
  if (!name) return false;
  const n = name.trim().toLowerCase();
  return PUSHKARALU_ULB_NAMES.some((u) => n.includes(u.toLowerCase()));
};

/* ------------------------------------------------------------------ *
 * Access control — the Pushkaralu Monitoring Dashboard (the tab in the
 * WMS dashboard AND the standalone /app/ds-wms/pushkaralu route) is
 * restricted to specific users. Add usernames here to grant access.
 * ------------------------------------------------------------------ */
export const PUSHKARALU_ALLOWED_USERS = ['kavitha_123'];

// Logged-in username, read from the JWT the shell stores (the same token the
// API interceptor sends as x-access-token). Returns '' if absent/unparseable.
export const getCurrentUsername = (): string => {
  try {
    const token = localStorage.getItem('x-access-token');
    const payload = token?.split('.')[1];
    if (!payload) return '';
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return (JSON.parse(json)?.username || '').toString();
  } catch {
    return '';
  }
};

export const canSeePushkaralu = (): boolean =>
  PUSHKARALU_ALLOWED_USERS.includes(getCurrentUsername());
